import { useEffect, useMemo, useRef, useState } from 'react';
import { AddOutlined, ArrowBackOutlined, EditOutlined } from '@mui/icons-material';
import { Alert, Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type {
  AdministratorBinding,
  AdministratorBindingInput,
  AdministratorIdentityOption,
  AdministratorOptions,
  AdministratorPermissionOption,
  AdministratorRoleInput,
  AdministratorRoleOption,
  AdministratorScopeType,
  AdministratorPageData,
  AdministratorViewer,
} from '../administratorModel';
import type { AdministratorPort } from '../administratorPort';
import { RolePermissionSelector } from '../RolePermissionSelector';
import { permissionLabel } from '../permissionLabels';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { useUnsavedChangesGuard } from '../../../shared/useUnsavedChangesGuard';
import {
  IdentityAvatar,
  FeedbackSnackbar,
  DefinitionList,
  DefinitionGrid,
  ConfirmDialog,
  DialogSurface,
  FormDialog,
  FormAutocomplete,
  FormTextField, HelpPopover,
  EditPage,
  InformationPage,
  PageSections,
  ResourcePage,
  PillTabs,
  ResourceCollection,
  SectionCard,
  StatusBadge,
  TableIdentityCell,
  ValueBadge,
  type ResourceCollectionState,
  type StatusTone,
} from '../../../design-system';

const fixtureProfilePhoto = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 64 64%22%3E%3Crect width=%2264%22 height=%2264%22 rx=%2232%22 fill=%22%23E8F3FF%22/%3E%3Ccircle cx=%2232%22 cy=%2225%22 r=%2212%22 fill=%22%232563EB%22/%3E%3Cpath d=%22M12 58c2-13 10-20 20-20s18 7 20 20%22 fill=%22%232563EB%22/%3E%3C/svg%3E';

const fixtureOptions: AdministratorOptions = {
  identities: [
    { identityId: 'identity-admin', personId: 'person-admin', email: 'admin@example.com', nickname: '제임스', profilePhotoUrl: fixtureProfilePhoto, eligible: true },
    { identityId: 'identity-hr', personId: 'person-hr', email: 'hr@example.com', nickname: '강예린', eligible: true },
  ],
  roles: [
    {
      id: 'role-admin', roleCode: 'HEYDAY_ADMIN', roleDescriptor: 'HRIS 전체 관리자',
      permissions: ['security.read', 'security.manage', 'staffing.read', 'staffing.write', 'tenant.settings.write'],
      scopeTypes: ['TENANT'], system: true, activeAdministratorCount: 1,
    },
    {
      id: 'role-system', roleCode: 'SYSTEM_ADMIN', roleDescriptor: '시스템 설정 관리자',
      permissions: ['security.read', 'tenant.settings.read', 'tenant.settings.write', 'certificates.settings.read', 'certificates.settings.write'],
      scopeTypes: ['TENANT'], system: true, activeAdministratorCount: 0,
    },
    {
      id: 'role-hr-operations', roleCode: 'HR_OPERATIONS_ADMIN', roleDescriptor: 'HR 운영 관리자',
      permissions: [
        'staffing.read', 'staffing.write', 'person.read', 'person.write', 'person.name.read', 'person.work_contact.read',
        'business_process.read', 'business_process.approve', 'business_process.cancel', 'absence.read', 'absence.write',
        'absence.manage', 'lifecycle.read', 'lifecycle.write', 'documents.read', 'documents.write', 'reports.read',
        'headcount.read', 'headcount.write',
      ],
      scopeTypes: ['TENANT', 'SUPERVISORY_ORGANIZATION'], system: true, activeAdministratorCount: 0,
    },
    {
      id: 'role-recruiting', roleCode: 'RECRUITING_ADMIN', roleDescriptor: '채용 관리자',
      permissions: ['recruiting.read', 'recruiting.write', 'recruiting.private.read', 'recruiting.private.write', 'recruiting.admin'],
      scopeTypes: ['TENANT'], system: true, activeAdministratorCount: 0,
    },
    {
      id: 'role-certificate', roleCode: 'CERTIFICATE_ADMIN', roleDescriptor: '증명서 운영 관리자',
      permissions: ['certificates.operations.read', 'certificates.issue', 'certificates.revoke'],
      scopeTypes: ['TENANT'], system: true, activeAdministratorCount: 0,
    },
    {
      id: 'role-custom-hr', roleCode: 'CUSTOM_HR_PARTNER', roleDescriptor: 'HR 파트너',
      permissions: ['person.read', 'staffing.read'], scopeTypes: ['TENANT'], system: false, activeAdministratorCount: 0,
    },
  ],
  permissions: [
    { permissionCode: 'security.read', domainCode: 'SECURITY_ADMINISTRATION', domainDescriptor: '관리자·보안' },
    { permissionCode: 'security.manage', domainCode: 'SECURITY_ADMINISTRATION', domainDescriptor: '관리자·보안' },
    { permissionCode: 'person.read', domainCode: 'PERSON', domainDescriptor: '구성원 정보' },
    { permissionCode: 'staffing.read', domainCode: 'STAFFING', domainDescriptor: '인사 운영' },
    { permissionCode: 'staffing.write', domainCode: 'STAFFING', domainDescriptor: '인사 운영' },
    { permissionCode: 'tenant.settings.read', domainCode: 'TENANT_SETTINGS', domainDescriptor: '회사 설정' },
    { permissionCode: 'tenant.settings.write', domainCode: 'TENANT_SETTINGS', domainDescriptor: '회사 설정' },
  ],
};

function fixtureData(viewer: AdministratorViewer): AdministratorPageData {
  const personId = viewer.personId || 'person-admin';
  return {
    bindings: [{
      id: 'binding-admin', identityId: 'identity-admin', personId,
      email: 'admin@example.com', roleCode: 'HEYDAY_ADMIN', roleDescriptor: 'HRIS 전체 관리자',
      permissions: fixtureOptions.roles[0].permissions,
      scope: { type: 'TENANT', includeDescendants: false },
      startsOn: '2026-08-01', grantedByPersonId: personId, grantedAt: '2026-08-01T09:00:00+09:00', status: 'ACTIVE',
    }],
    options: {
      ...fixtureOptions,
      identities: fixtureOptions.identities.map((identity, index) => index === 0 ? { ...identity, personId } : identity),
    },
    organizations: [],
  };
}

function todayInSeoul() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function emptyInput(): AdministratorBindingInput {
  return {
    identityId: '', roleCode: '', scope: { type: 'TENANT', includeDescendants: false },
    startsOn: todayInSeoul(), endsOn: '',
  };
}

function statusPresentation(status: AdministratorBinding['status']): { label: string; tone: StatusTone } {
  if (status === 'ACTIVE') return { label: '사용 중', tone: 'success' };
  if (status === 'SCHEDULED') return { label: '시작 예정', tone: 'info' };
  if (status === 'EXPIRED') return { label: '기간 종료', tone: 'neutral' };
  return { label: '회수됨', tone: 'danger' };
}

const permissionCategoryLabels: Record<string, string> = {
  security: '관리자·보안', staffing: '인사 운영', person: '구성원 정보', business_process: '결재',
  absence: '휴가', documents: '문서', certificates: '증명서', lifecycle: '온·오프보딩',
  rewards: '보상', headcount: '정원', recruiting: '채용', tenant: '회사 설정', workflow: '워크플로우',
  bulk: '대량 작업', reports: '보고서', directory: '조직도',
};

function permissionCategories(permissions: readonly string[]) {
  const values = new Set<string>();
  for (const permission of permissions) {
    const prefix = permission.split('.')[0];
    values.add(permissionCategoryLabels[prefix] ?? '기타');
  }
  return [...values];
}

function permissionGroups(options: readonly AdministratorPermissionOption[]) {
  const groups = new Map<string, AdministratorPermissionOption[]>();
  for (const option of options) {
    const prefix = option.permissionCode.split('.')[0];
    const label = permissionCategoryLabels[prefix] ?? option.domainDescriptor ?? '기타';
    groups.set(label, [...(groups.get(label) ?? []), option]);
  }
  return [...groups.entries()].map(([label, permissions]) => ({ label, permissions }));
}

function emptyRoleInput(): AdministratorRoleInput {
  return { roleDescriptor: '', permissions: [] };
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

export type AdministratorManagementPageProps = {
  port: AdministratorPort;
  viewer: AdministratorViewer;
  fixtureMode: boolean;
  canManage: boolean;
  canReadStaffing: boolean;
};

export function AdministratorManagementPage({
  port,
  viewer,
  fixtureMode,
  canManage,
  canReadStaffing,
}: AdministratorManagementPageProps) {
  const resource = useAsyncResource<AdministratorPageData>(async () => {
    if (fixtureMode) return fixtureData(viewer);
    return port.load(canReadStaffing);
  }, [canReadStaffing, port, fixtureMode, viewer.personId]);
  const data = resource.data ?? { bindings: [], options: { identities: [], roles: [], permissions: [] }, organizations: [] };
  const organizationsById = useMemo(() => new Map(data.organizations.map((organization) => [organization.id, organization])), [data.organizations]);
  const identitiesById = useMemo(() => new Map(data.options.identities.map((identity) => [identity.identityId, identity])), [data.options.identities]);
  const rolesByCode = useMemo(() => new Map(data.options.roles.map((role) => [role.roleCode, role])), [data.options.roles]);
  const permissionOptionGroups = useMemo(() => permissionGroups(data.options.permissions), [data.options.permissions]);
  const [activeView, setActiveView] = useState<'administrators' | 'roles'>('administrators');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [selected, setSelected] = useState<AdministratorBinding>();
  const [dialogMode, setDialogMode] = useState<'CREATE' | 'EDIT'>();
  const [input, setInput] = useState<AdministratorBindingInput>(emptyInput);
  const [bindingSearch, setBindingSearch] = useState({ identity: '', role: '', organization: '' });
  const [mutationBusy, setMutationBusy] = useState(false);
  const [mutationError, setMutationError] = useState<string>();
  const [pageNotice, setPageNotice] = useState<string>();
  const [revokeOpen, setRevokeOpen] = useState(false);
  const bindingStepRef = useRef<HTMLDivElement>(null);
  const previousBindingStep = useRef('detail');
  const bindingStep = dialogMode === 'EDIT' ? 'edit' : revokeOpen ? 'revoke' : 'detail';
  useEffect(() => {
    if (selected && previousBindingStep.current !== bindingStep) bindingStepRef.current?.focus();
    previousBindingStep.current = bindingStep;
  }, [bindingStep, selected]);
  const [roleQuery, setRoleQuery] = useState('');
  const [selectedRoleDefinition, setSelectedRoleDefinition] = useState<AdministratorRoleOption>();
  const [roleDialogMode, setRoleDialogMode] = useState<'CREATE' | 'EDIT'>();
  const [roleInput, setRoleInput] = useState<AdministratorRoleInput>(emptyRoleInput);
  const [roleInitialInput, setRoleInitialInput] = useState<AdministratorRoleInput>(emptyRoleInput);
  const [discardRoleOpen, setDiscardRoleOpen] = useState(false);
  const [roleMutationBusy, setRoleMutationBusy] = useState(false);
  const [roleMutationError, setRoleMutationError] = useState<string>();
  const roleTitleRef = useRef<HTMLHeadingElement>(null);
  const listTitleRef = useRef<HTMLHeadingElement>(null);
  const roleReturnFocus = useRef<HTMLElement | null>(null);
  const rememberRoleRowFocus = (event: { target: EventTarget | null }) => {
    const cell = event.target instanceof Element ? event.target.closest<HTMLElement>('[role="gridcell"]') : null;
    if (cell) roleReturnFocus.current = cell;
  };
  useEffect(() => {
    if (roleDialogMode || selectedRoleDefinition) {
      roleTitleRef.current?.focus();
      return;
    }
    // 숨김 해제 스타일과 inert 변경이 반영된 프레임 뒤에 목록 초점을 복원합니다.
    let frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => {
        if (roleReturnFocus.current) (roleReturnFocus.current.isConnected ? roleReturnFocus.current : listTitleRef.current)?.focus({ preventScroll: true });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [roleDialogMode, selectedRoleDefinition]);
  const selectedRole = rolesByCode.get(input.roleCode);
  const roleDirty = Boolean(roleDialogMode) && (roleInput.roleDescriptor !== roleInitialInput.roleDescriptor || [...roleInput.permissions].sort().join('\n') !== [...roleInitialInput.permissions].sort().join('\n'));
  useUnsavedChangesGuard({ dirty: roleDirty, busy: roleMutationBusy || mutationBusy });
  const eligibleIdentities = data.options.identities.filter((identity) => identity.eligible);

  const administratorName = (personId: string, identity?: AdministratorIdentityOption) => {
    const option = identity ?? data.options.identities.find((candidate) => candidate.personId === personId);
    return option?.nickname || option?.email || '연결된 관리자';
  };
  const administratorAvatar = (personId: string, identity?: AdministratorIdentityOption) => {
    const option = identity ?? data.options.identities.find((candidate) => candidate.personId === personId);
    const name = administratorName(personId, option);
    return <IdentityAvatar
      src={option?.profilePhotoUrl}
      alt={option?.profilePhotoUrl ? `${name} 프로필 사진` : undefined}
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 750,
      }}
    >{Array.from(name)[0] ?? '?'}</IdentityAvatar>;
  };
  const scopeLabel = (binding: AdministratorBinding) => {
    if (binding.scope.type === 'TENANT') return '전체 회사';
    const organization = binding.scope.organizationId ? organizationsById.get(binding.scope.organizationId) : undefined;
    return `${organization?.name ?? '조직 범위'}${binding.scope.includeDescendants ? ' 및 하위 조직' : ''}`;
  };
  const rows = data.bindings.filter((binding) => {
    const haystack = `${administratorName(binding.personId, identitiesById.get(binding.identityId))} ${binding.email} ${binding.roleDescriptor} ${scopeLabel(binding)}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase()) && (!statusFilter || binding.status === statusFilter);
  });
  const openCreate = () => {
    if (!canManage || mutationBusy) return;
    const next = emptyInput();
    next.roleCode = data.options.roles[0]?.roleCode ?? '';
    setBindingSearch({ identity: '', role: data.options.roles[0]?.roleDescriptor ?? '', organization: '' });
    setInput(next); setMutationError(undefined); setDialogMode('CREATE');
  };
  const openEdit = () => {
    if (!canManage || !selected || selected.status === 'REVOKED' || selfFullAdministrator || mutationBusy) return;
    setBindingSearch({
      identity: identitiesById.get(selected.identityId)?.nickname || selected.email,
      role: rolesByCode.get(selected.roleCode)?.roleDescriptor ?? selected.roleDescriptor,
      organization: selected.scope.organizationId ? organizationsById.get(selected.scope.organizationId)?.name ?? '현재 지정 조직' : '',
    });
    setInput({
      identityId: selected.identityId, roleCode: selected.roleCode, scope: { ...selected.scope },
      startsOn: selected.startsOn, endsOn: selected.endsOn ?? '',
    });
    setMutationError(undefined); setDialogMode('EDIT');
  };
  const closeDialog = () => { if (!mutationBusy) { setDialogMode(undefined); setMutationError(undefined); } };
  const closeBindingDetail = () => {
    if (mutationBusy) return;
    if (dialogMode === 'EDIT') setDialogMode(undefined);
    else if (revokeOpen) setRevokeOpen(false);
    else setSelected(undefined);
    setMutationError(undefined);
  };
  const mutationValid = Boolean(input.identityId && input.roleCode && input.startsOn && (
    input.scope.type === 'TENANT' || input.scope.organizationId
  ) && (!input.endsOn || input.endsOn >= input.startsOn));
  const save = async () => {
    if (!canManage || !dialogMode || !mutationValid || mutationBusy || (dialogMode === 'EDIT' && (!selected || selected.status === 'REVOKED' || selfFullAdministrator))) return;
    setMutationBusy(true); setMutationError(undefined); setPageNotice(undefined);
    try {
      if (dialogMode === 'CREATE') await port.create(input);
      else await port.update(selected?.id ?? '', input);
      setDialogMode(undefined);
      setSelected(undefined);
      setPageNotice(dialogMode === 'CREATE' ? '관리자를 지정했습니다.' : '관리자 역할과 범위를 변경했습니다. 대상 사용자의 기존 세션은 종료되었습니다.');
      resource.reload();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : '관리자 권한을 저장하지 못했습니다.');
    } finally {
      setMutationBusy(false);
    }
  };
  const revoke = async () => {
    if (!canManage || !selected || selected.status === 'REVOKED' || selfFullAdministrator || mutationBusy) return;
    setMutationBusy(true); setMutationError(undefined); setPageNotice(undefined);
    try {
      await port.revoke(selected.id);
      setRevokeOpen(false); setSelected(undefined);
      setPageNotice('관리자 권한을 회수하고 대상 사용자의 기존 세션을 종료했습니다.');
      resource.reload();
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : '관리자 권한을 회수하지 못했습니다.');
    } finally {
      setMutationBusy(false);
    }
  };
  const roleRows = data.options.roles.filter((role) => {
    const haystack = `${role.roleDescriptor} ${role.roleCode} ${role.permissions.map(permissionLabel).join(' ')} ${role.permissions.join(' ')}`.toLowerCase();
    return haystack.includes(roleQuery.trim().toLowerCase());
  });
  const openRoleCreate = () => {
    if (!canManage) return;
    roleReturnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRoleInput(emptyRoleInput());
    setRoleInitialInput(emptyRoleInput());
    setRoleMutationError(undefined);
    setRoleDialogMode('CREATE');
  };
  const openRoleEdit = () => {
    if (!canManage || !selectedRoleDefinition || selectedRoleDefinition.system) return;
    const next = {
      roleDescriptor: selectedRoleDefinition.roleDescriptor,
      permissions: [...selectedRoleDefinition.permissions],
    };
    setRoleInput(next);
    setRoleInitialInput(next);
    setRoleMutationError(undefined);
    setRoleDialogMode('EDIT');
  };
  const closeRoleDialog = () => {
    setRoleDialogMode(undefined);
    setRoleMutationError(undefined);
    setDiscardRoleOpen(false);
  };
  const cancelRoleEdit = () => {
    if (roleMutationBusy) return;
    if (roleDirty) setDiscardRoleOpen(true);
    else closeRoleDialog();
  };
  const roleMutationValid = roleInput.roleDescriptor.trim().length >= 2 && roleInput.permissions.length > 0;
  const toggleRolePermission = (permissionCode: string, checked: boolean) => {
    setRoleInput((current) => ({
      ...current,
      permissions: checked
        ? [...current.permissions, permissionCode]
        : current.permissions.filter((permission) => permission !== permissionCode),
    }));
  };
  const saveRole = async () => {
    if (!canManage || !roleDialogMode || !roleMutationValid || roleMutationBusy) return;
    setRoleMutationBusy(true); setRoleMutationError(undefined); setPageNotice(undefined);
    try {
      const saved = roleDialogMode === 'CREATE'
        ? await port.createRole(roleInput)
        : await port.updateRole(selectedRoleDefinition?.roleCode ?? '', roleInput);
      closeRoleDialog();
      setSelectedRoleDefinition(saved);
      setPageNotice(roleDialogMode === 'CREATE'
        ? '새 관리자 역할을 만들었습니다.'
        : '관리자 역할을 변경했습니다. 이 역할을 사용하는 관리자는 다시 로그인해야 합니다.');
      resource.reload();
    } catch (error) {
      setRoleMutationError(error instanceof Error ? error.message : '관리자 역할을 저장하지 못했습니다.');
    } finally {
      setRoleMutationBusy(false);
    }
  };
  const selfFullAdministrator = selected?.roleCode === 'HEYDAY_ADMIN' && selected.personId === viewer.personId;
  const assignmentUnavailableReason = !resource.loading && !resource.error && canManage
    ? !data.options.roles.length
      ? '지정할 수 있는 관리자 역할이 없습니다. 역할을 먼저 만들어 주세요.'
      : !eligibleIdentities.length
        ? '관리자로 지정할 수 있는 구성원이 없습니다. Google 계정이 연결된 현재 재직 구성원만 지정할 수 있습니다.'
        : undefined
    : undefined;
  const columns: GridColDef<AdministratorBinding>[] = [
    { field: 'administrator', headerName: '관리자', minWidth: 240, flex: 1.2, renderCell: ({ row }) => {
      const identity = identitiesById.get(row.identityId);
      const name = administratorName(row.personId, identity);
      return <TableIdentityCell
        primary={`${name}${row.personId === viewer.personId ? ' (나)' : ''}`}
        secondary={row.email || identity?.email || 'Google 계정 연결됨'}
        leading={administratorAvatar(row.personId, identity)}
      />;
    } },
    { field: 'roleDescriptor', headerName: '관리자 역할', minWidth: 170, flex: 0.9, renderCell: ({ value }) => <ValueBadge label={value} maxWidth="100%" /> },
    { field: 'scope', headerName: '관리 범위', minWidth: 180, flex: 1, renderCell: ({ row }) => row.scope.type === 'TENANT' ? <ValueBadge label={scopeLabel(row)} /> : <Typography variant="body2" noWrap>{scopeLabel(row)}</Typography> },
    { field: 'period', headerName: '효력 기간', minWidth: 160, flex: 0.8, renderCell: ({ row }) => <Typography variant="body2">{row.startsOn} ~ {row.endsOn || '계속'}</Typography> },
    { field: 'grantedAt', headerName: '권한 부여일', width: 130, renderCell: ({ row }) => <Typography variant="body2">{row.grantedAt.slice(0, 10)}</Typography> },
    { field: 'status', headerName: '상태', width: 105, renderCell: ({ row }) => { const status = statusPresentation(row.status); return <StatusBadge label={status.label} tone={status.tone} />; } },
  ];
  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? { kind: 'empty', title: data.bindings.length ? '검색 조건에 맞는 관리자가 없습니다.' : '지정된 관리자가 없습니다.', description: canManage ? '재직 구성원에게 관리자 역할을 지정해 주세요.' : undefined }
        : { kind: 'ready' };
  const roleColumns: GridColDef<AdministratorRoleOption>[] = [
    { field: 'roleDescriptor', headerName: '역할', minWidth: 220, flex: 1.2, renderCell: ({ row }) => <Stack spacing={0.5} sx={{ minWidth: 0, justifyContent: 'center', alignItems: 'flex-start' }}><ValueBadge label={row.roleDescriptor} /><ValueBadge size="compact" label={row.system ? '기본 제공 역할' : '사용자 지정 역할'} /></Stack> },
    { field: 'areas', headerName: '사용 가능 영역', minWidth: 220, flex: 1.2, renderCell: ({ row }) => permissionCategories(row.permissions).length ? <ValueBadge label={permissionCategories(row.permissions).join(' · ')} /> : '없음' },
    { field: 'permissions', headerName: '권한', width: 100, type: 'number', valueGetter: (_value, row) => row.permissions.length, valueFormatter: (value) => `${value}개` },
    { field: 'activeAdministratorCount', headerName: '사용 중 관리자', width: 130, type: 'number', valueFormatter: (value) => `${value}명` },
  ];
  const roleCollectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : roleRows.length === 0
        ? { kind: 'empty', title: data.options.roles.length ? '검색 조건에 맞는 역할이 없습니다.' : '관리자 역할이 없습니다.', description: canManage ? '업무에 필요한 권한을 묶어 사용자 지정 역할을 만들어 주세요.' : undefined }
        : { kind: 'ready' };

  const bindingFields = <>
      {mutationError && <Alert severity="error">{mutationError}</Alert>}
      <FormAutocomplete
        options={eligibleIdentities}
        value={identitiesById.get(input.identityId) ?? null}
        inputValue={bindingSearch.identity}
        disabled={dialogMode === 'EDIT'}
        getOptionLabel={(identity) => administratorName(identity.personId, identity)}
        getOptionKey={(identity) => identity.identityId}
        isOptionEqualToValue={(option, value) => option.identityId === value.identityId}
        filterOptions={(options, state) => options.filter((identity) => `${administratorName(identity.personId, identity)} ${identity.email}`.toLocaleLowerCase().includes(state.inputValue.trim().toLocaleLowerCase()))}
        onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setBindingSearch((current) => ({ ...current, identity: value })); setInput((current) => ({ ...current, identityId: '' })); } }}
        onChange={(_, identity) => { setInput((current) => ({ ...current, identityId: identity?.identityId ?? '' })); setBindingSearch((current) => ({ ...current, identity: identity ? administratorName(identity.personId, identity) : '' })); }}
        input={{ required: true, label: '구성원', placeholder: '이름 또는 이메일로 검색', helperText: dialogMode === 'EDIT' ? '관리자 대상은 변경할 수 없습니다.' : 'Google 계정이 연결된 재직 구성원만 선택할 수 있습니다.' }}
        noOptionsText="선택할 수 있는 구성원이 없습니다."
        renderOption={({ key, ...props }, identity) => <Box component="li" key={key} {...props}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            {administratorAvatar(identity.personId, identity)}
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{administratorName(identity.personId, identity)}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{identity.email}</Typography>
            </Stack>
          </Stack>
        </Box>}
      />
      <FormAutocomplete options={data.options.roles} value={selectedRole ?? null} inputValue={bindingSearch.role}
        getOptionLabel={(role) => role.roleDescriptor} getOptionKey={(role) => role.roleCode} isOptionEqualToValue={(option, value) => option.roleCode === value.roleCode}
        onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setBindingSearch((current) => ({ ...current, role: value, organization: '' })); setInput((current) => ({ ...current, roleCode: '', scope: { type: 'TENANT', includeDescendants: false } })); } }}
        onChange={(_, role) => {
        const scopeType = role?.scopeTypes.includes(input.scope.type) ? input.scope.type : 'TENANT';
        setInput((current) => ({ ...current, roleCode: role?.roleCode ?? '', scope: { type: scopeType, includeDescendants: false } }));
        setBindingSearch((current) => ({ ...current, role: role?.roleDescriptor ?? '', organization: '' }));
      }} input={{ required: true, label: '관리자 역할', placeholder: '역할 이름으로 검색' }} noOptionsText="검색 결과가 없습니다." />
      {selectedRole && <Alert severity="info">{permissionCategories(selectedRole.permissions).join(' · ')} 영역에 접근할 수 있습니다.</Alert>}
      <FormTextField select required label="관리 범위" value={input.scope.type} onChange={(event) => { setInput((current) => ({ ...current, scope: { type: event.target.value as AdministratorScopeType, includeDescendants: false } })); setBindingSearch((current) => ({ ...current, organization: '' })); }}>
        {(selectedRole?.scopeTypes ?? [input.scope.type]).map((scopeType) => <MenuItem key={scopeType} value={scopeType}><ValueBadge label={scopeType === 'TENANT' ? '전체 회사' : '특정 조직'} /></MenuItem>)}
      </FormTextField>
      {input.scope.type === 'SUPERVISORY_ORGANIZATION' && <>
        <FormAutocomplete options={data.organizations} value={organizationsById.get(input.scope.organizationId ?? '') ?? null} inputValue={bindingSearch.organization}
          disabled={!fixtureMode && !canReadStaffing}
          getOptionLabel={(organization) => organization.name} getOptionKey={(organization) => organization.id} isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setBindingSearch((current) => ({ ...current, organization: value })); setInput((current) => ({ ...current, scope: { ...current.scope, organizationId: undefined } })); } }}
          onChange={(_, organization) => { setInput((current) => ({ ...current, scope: { ...current.scope, organizationId: organization?.id } })); setBindingSearch((current) => ({ ...current, organization: organization?.name ?? '' })); }}
          input={{ required: true, label: '대상 조직', placeholder: '조직명으로 검색', helperText: !fixtureMode && !canReadStaffing ? '조직을 선택하려면 조직 조회 권한이 필요합니다.' : undefined }}
          noOptionsText="선택할 수 있는 조직이 없습니다." />
        <FormTextField select label="하위 조직" value={input.scope.includeDescendants ? 'INCLUDE' : 'EXCLUDE'} onChange={(event) => setInput((current) => ({ ...current, scope: { ...current.scope, includeDescendants: event.target.value === 'INCLUDE' } }))}>
          <MenuItem value="INCLUDE"><ValueBadge label="하위 조직 포함" /></MenuItem><MenuItem value="EXCLUDE"><ValueBadge label="선택한 조직만" /></MenuItem>
        </FormTextField>
      </>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <FormTextField type="date" required label="효력 시작일" value={input.startsOn} onChange={(event) => setInput((current) => ({ ...current, startsOn: event.target.value }))} />
        <FormTextField type="date" label="효력 종료일" value={input.endsOn ?? ''} help="비워두면 계속 유지됩니다." slotProps={{ htmlInput: { min: input.startsOn } }} onChange={(event) => setInput((current) => ({ ...current, endsOn: event.target.value }))} />
      </Box>
  </>;

  return <Box sx={{ position: 'relative', height: '100%', minHeight: 0, minWidth: 0 }}>
    <Box inert={Boolean(selectedRoleDefinition || roleDialogMode) || undefined} aria-hidden={Boolean(selectedRoleDefinition || roleDialogMode) || undefined} sx={{ height: '100%', minHeight: 0, minWidth: 0, position: selectedRoleDefinition || roleDialogMode ? 'absolute' : 'relative', inset: selectedRoleDefinition || roleDialogMode ? 0 : 'auto', visibility: selectedRoleDefinition || roleDialogMode ? 'hidden' : 'visible' }}>
    <ResourcePage
      title="관리자 설정"
      titleRef={listTitleRef}
      primaryAction={canManage ? <Button
        variant="fill"
        startIcon={<AddOutlined />}
        disabled={activeView === 'administrators' && (!data.options.roles.length || !eligibleIdentities.length)}
        onClick={activeView === 'administrators' ? openCreate : openRoleCreate}
      >{activeView === 'administrators' ? '관리자 지정' : '역할 만들기'}</Button> : undefined}
      tabs={<PillTabs
        value={activeView}
        ariaLabel="관리자 설정 세부 메뉴"
        items={[
          { value: 'administrators', label: '관리자', tabId: 'administrator-tab', panelId: 'administrator-panel' },
          { value: 'roles', label: '역할', tabId: 'administrator-role-tab', panelId: 'administrator-role-panel' },
        ]}
        onChange={setActiveView}
      />}
    >
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {activeView === 'administrators' ? <Box id="administrator-panel" role="tabpanel" aria-labelledby="administrator-tab" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {assignmentUnavailableReason && <Alert severity="info" sx={{ mb: 1.5, flexShrink: 0 }}>{assignmentUnavailableReason}</Alert>}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResourceCollection
              state={collectionState}
              rows={rows}
              totalCount={data.bindings.length}
              columns={columns}
              query={query}
              onQueryChange={setQuery}
              searchPlaceholder="이름·이메일·역할·조직 검색"
              searchLabel="관리자 검색"
              ariaLabel="관리자 목록"
              filters={[{ label: '상태', value: statusFilter, onChange: setStatusFilter, defaultValue: 'ACTIVE', options: [
                { label: '사용 중', value: 'ACTIVE' }, { label: '전체', value: '' }, { label: '시작 예정', value: 'SCHEDULED' },
                { label: '기간 종료', value: 'EXPIRED' }, { label: '회수됨', value: 'REVOKED' },
              ] }]}
              onRowClick={({ row }) => setSelected(row)}
            />
          </Box>
        </Box> : <Box id="administrator-role-panel" role="tabpanel" aria-labelledby="administrator-role-tab" onClickCapture={rememberRoleRowFocus} onKeyDownCapture={rememberRoleRowFocus} sx={{ flex: 1, minHeight: 0 }}>
          <ResourceCollection
            state={roleCollectionState}
            rows={roleRows}
            totalCount={data.options.roles.length}
            columns={roleColumns}
            rowHeight={56}
            query={roleQuery}
            onQueryChange={setRoleQuery}
            searchPlaceholder="역할 이름·권한 검색"
            searchLabel="관리자 역할 검색"
            ariaLabel="관리자 역할 목록"
            onRowClick={({ row }) => setSelectedRoleDefinition(row)}
          />
        </Box>}
      </Box>
    </ResourcePage>
    </Box>

    <FeedbackSnackbar open={Boolean(pageNotice)} message={pageNotice} tone="success" onClose={() => setPageNotice(undefined)} />

    <DialogSurface
      open={Boolean(selected)}
      onClose={closeBindingDetail}
      title={dialogMode === 'EDIT' ? '관리자 역할 수정' : revokeOpen ? '관리자 권한 회수' : selected ? administratorName(selected.personId, identitiesById.get(selected.identityId)) : ''}
      description={selected?.email}
      titleLeading={bindingStep === 'detail' && selected ? administratorAvatar(selected.personId, identitiesById.get(selected.identityId)) : undefined}
      titleAdornment={bindingStep === 'detail' && selected ? <StatusBadge {...statusPresentation(selected.status)} /> : undefined}
      closeLabel={bindingStep === 'detail' ? '상세 닫기' : '상세로 돌아가기'}
      busy={mutationBusy}
      size="standard"
      onSubmit={dialogMode === 'EDIT' ? save : revokeOpen ? revoke : undefined}
      actions={dialogMode === 'EDIT' ? <>
        <Button variant="outlined" color="inherit" disabled={mutationBusy} onClick={closeBindingDetail}>취소</Button>
        <Button variant="contained" type="submit" disabled={mutationBusy || !mutationValid}>{mutationBusy ? '저장 중' : '변경 저장'}</Button>
      </> : revokeOpen ? <>
        <Button variant="outlined" color="inherit" disabled={mutationBusy} onClick={closeBindingDetail}>취소</Button>
        <Button variant="contained" color="error" type="submit" disabled={mutationBusy}>{mutationBusy ? '회수 중' : '권한 회수'}</Button>
      </> : canManage && selected && selected.status !== 'REVOKED' ? <>
        <Button variant="outlined" startIcon={<EditOutlined />} disabled={selfFullAdministrator} onClick={openEdit}>역할 수정</Button>
        <Button color="error" variant="outlined" disabled={selfFullAdministrator} onClick={() => { setMutationError(undefined); setRevokeOpen(true); }}>권한 회수</Button>
      </> : undefined}
    >
      <Box ref={bindingStepRef} tabIndex={-1} sx={{ outline: 'none' }}>
      {dialogMode === 'EDIT' ? <Box component="fieldset" disabled={mutationBusy} sx={{ m: 0, minWidth: 0, border: 0, p: 0 }}>
        <Stack spacing={3}>{bindingFields}</Stack>
      </Box> : revokeOpen ? <Stack spacing={2}>
        {mutationError && <Alert severity="error">{mutationError}</Alert>}
        <Alert severity="warning">{selected ? administratorName(selected.personId, identitiesById.get(selected.identityId)) : '선택한 관리자'}의 {selected?.roleDescriptor} 권한을 회수합니다. 기존 로그인 세션도 함께 종료됩니다.</Alert>
      </Stack> : selected && <Stack spacing={2}>
        <DefinitionList items={[
          ['관리자 역할', <ValueBadge label={selected.roleDescriptor} />],
          ['관리 범위', selected.scope.type === 'TENANT' ? <ValueBadge label={scopeLabel(selected)} /> : scopeLabel(selected)],
          ['효력 기간', `${selected.startsOn} ~ ${selected.endsOn || '계속'}`],
          ['권한 부여일', formatDateTime(selected.grantedAt)],
          ['권한 부여자', selected.grantedByPersonId ? administratorName(selected.grantedByPersonId) : '시스템'],
        ]} />
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}><Typography component="h3" variant="subtitle2">사용 가능 영역</Typography><HelpPopover title="사용 가능 영역">메뉴는 역할에 포함된 권한에 따라 표시됩니다.</HelpPopover></Stack>
          <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            {permissionCategories(selected.permissions).map((label) => <ValueBadge key={label} label={label} />)}
          </Stack>
        </Stack>
        {selfFullAdministrator && <Typography variant="caption" color="text.secondary">본인의 전체 관리자 권한은 다른 전체 관리자가 변경해야 합니다.</Typography>}
      </Stack>}
      </Box>
    </DialogSurface>

    {selectedRoleDefinition && !roleDialogMode && <InformationPage
      title={selectedRoleDefinition.roleDescriptor}
      titleRef={roleTitleRef}
      width="wide"
      navigation={<Button startIcon={<ArrowBackOutlined />} onClick={() => { setSelectedRoleDefinition(undefined); setRoleMutationError(undefined); }}>역할 목록</Button>}
      primaryAction={canManage && !selectedRoleDefinition.system ? <Button variant="fill" startIcon={<EditOutlined />} onClick={openRoleEdit}>역할 편집</Button> : undefined}
      primaryActionVisibility="always"
    >
      <PageSections>
        <SectionCard title="역할 정보" bodyPadding="standard">
        <DefinitionGrid items={[
          { label: '역할 유형', value: <ValueBadge label={selectedRoleDefinition.system ? '기본 제공 역할' : '사용자 지정 역할'} /> },
          { label: '관리 범위', value: <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>{selectedRoleDefinition.scopeTypes.map((scope) => <ValueBadge key={scope} label={scope === 'TENANT' ? '전체 회사' : '특정 조직'} />)}</Stack> },
          { label: '사용 중 관리자', value: `${selectedRoleDefinition.activeAdministratorCount}명` },
          { label: '포함된 권한', value: `${selectedRoleDefinition.permissions.length}개` },
        ]} />
        </SectionCard>
        <SectionCard title="사용 가능 영역" bodyPadding="standard">
          <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            {permissionCategories(selectedRoleDefinition.permissions).map((label) => <ValueBadge key={label} label={label} />)}
          </Stack>
        </SectionCard>
        <SectionCard title="세부 권한" bodyPadding="standard">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            {permissionGroups(selectedRoleDefinition.permissions.map((permissionCode) => ({ permissionCode, domainCode: permissionCode.split('.')[0], domainDescriptor: '기타' }))).map((group) => <Stack key={group.label} spacing={1}>
              <Typography component="h3" variant="subtitle2">{group.label}</Typography>
              <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                {group.permissions.map(({ permissionCode }) => <ValueBadge key={permissionCode} label={permissionLabel(permissionCode)} maxWidth="100%" />)}
              </Stack>
            </Stack>)}
          </Box>
        </SectionCard>
        {selectedRoleDefinition.system
          ? <Alert severity="info">기본 제공 역할은 Heyday 권한 정책과 함께 관리되어 이 화면에서 변경할 수 없습니다.</Alert>
          : <Alert severity="warning">역할 권한을 변경하면 이 역할을 사용하는 관리자의 기존 세션이 종료됩니다.</Alert>}
      </PageSections>
    </InformationPage>}

    <FormDialog
      open={dialogMode === 'CREATE'}
      onClose={closeDialog}
      onSubmit={save}
      title="관리자 지정"
      cancelLabel="취소"
      submitLabel="관리자 지정"
      busy={mutationBusy}
      busyLabel="저장 중"
      submitDisabled={!mutationValid}
    >
      {bindingFields}
    </FormDialog>

    {roleDialogMode && <EditPage
      title={roleDialogMode === 'CREATE' ? '관리자 역할 만들기' : '관리자 역할 편집'}
      titleRef={roleTitleRef}
      width="wide" presentation="sectioned"
      navigation={<Button startIcon={<ArrowBackOutlined />} onClick={cancelRoleEdit} disabled={roleMutationBusy}>{roleDialogMode === 'EDIT' ? '역할 상세' : '역할 목록'}</Button>}
      editActions={<>
        <Button color="inherit" variant="outlined" onClick={cancelRoleEdit} disabled={roleMutationBusy}>취소</Button>
        <Button variant="fill" type="submit" form="administrator-role-form" disabled={!canManage || !roleMutationValid || roleMutationBusy}>{roleMutationBusy ? '저장 중…' : roleDialogMode === 'CREATE' ? '역할 만들기' : '변경 저장'}</Button>
      </>}
    >
      <Box component="form" id="administrator-role-form" onSubmit={(event) => { event.preventDefault(); void saveRole(); }}>
      <PageSections>
      {roleMutationError && <Alert severity="error">{roleMutationError}</Alert>}
      {roleDialogMode === 'EDIT' && <Alert severity="warning">저장하면 이 역할을 현재 사용하는 관리자의 기존 세션이 종료됩니다.</Alert>}
      <SectionCard title="역할 정보" bodyPadding="standard">
      <FormTextField
        required
        label="역할 이름"
        disabled={!canManage || roleMutationBusy}
        value={roleInput.roleDescriptor}
        slotProps={{ htmlInput: { maxLength: 80 } }}
        onChange={(event) => setRoleInput((current) => ({ ...current, roleDescriptor: event.target.value }))}
      />
      </SectionCard>
      <SectionCard title="업무 권한" bodyPadding="standard">
        <RolePermissionSelector groups={permissionOptionGroups} value={roleInput.permissions} disabled={!canManage || roleMutationBusy} onToggle={toggleRolePermission} />
      </SectionCard>
      </PageSections>
      </Box>
    </EditPage>}
    <ConfirmDialog open={discardRoleOpen} intent="discard" title="수정한 역할을 버릴까요?" description="저장하지 않은 역할 이름과 권한 선택이 사라집니다." onCancel={() => setDiscardRoleOpen(false)} onConfirm={closeRoleDialog} cancelLabel="계속 수정" confirmLabel="버리고 돌아가기" />


  </Box>;
}
